import { useState } from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import axios from "axios";
import { Formik, Form } from "formik";
import { Box, Grid, Typography } from "@material-ui/core";
import { toast } from "react-toastify";
import { fileToBase64 } from "utils/fileToBase64";
import { BANK_ACCOUNTS } from "constants/api";
import { initialValues, setFileValue, getValidationSchema } from "./formModel";
import {
  SrAlert,
  SrButton,
  SrInput,
  SrDialog,
  SrFileInput,
} from "../../../common";

const CreateBankAccountDialog = ({ handleSubmit, handleClose, open }) => {
  const [errorMessage, setErrorMessage] = useState();
  const intl = useIntl();

  const onSubmit = async (form) => {
    setErrorMessage();
    try {
      const accountStatement = await fileToBase64(form.accountStatement?.file);
      const identificationFront = await fileToBase64(
        form.identificationFront?.file
      );
      const identificationBack = await fileToBase64(
        form.identificationBack?.file
      );

      const response = await axios.post(BANK_ACCOUNTS, {
        type: "CLABE",
        alias: form.name,
        number: form.clabe,
        origin: "1",
        img: accountStatement,
        ife_front: identificationFront,
        ife_back: identificationBack,
      });

      if (response?.success) {
        handleSubmit(response?.data?.result);
        toast.success(intl.formatMessage({ id: "bankAccounts.accountAdded" }));
        handleClose();
      }
    } catch (error) {
      const message =
        error?.response?.data?.error?.message ??
        intl.formatMessage({ id: "errors.somethingWrong" });
      setErrorMessage(message);
    }
  };

  return (
    <SrDialog
      handleClose={handleClose}
      open={open}
      heading={intl.formatMessage({ id: "bankAccounts.addAccount" })}
    >
      <Formik
        initialValues={initialValues}
        validateOnChange
        validationSchema={getValidationSchema()}
        onSubmit={onSubmit}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          setFieldValue,
          touched,
          values,
          isSubmitting,
        }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SrInput
                  error={Boolean(errors.name && touched.name)}
                  label={intl.formatMessage({ id: "common.name" })}
                  name="name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder={intl.formatMessage({
                    id: "bankAccounts.entry.nameAccountHolder",
                  })}
                  value={values.name}
                  helperText={touched.name && errors.name}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <SrInput
                  error={Boolean(errors.clabe && touched.clabe)}
                  label={intl.formatMessage({ id: "common.clabe" })}
                  name="clabe"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder={intl.formatMessage({
                    id: "bankAccounts.entry.clabe",
                  })}
                  value={values.clabe}
                  helperText={touched.clabe && errors.clabe}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" component="h2">
                  {intl.formatMessage({ id: "bankAccounts.documents" })}
                </Typography>
                <SrAlert severity="info">
                  {intl.formatMessage({
                    id: "bankAccounts.uploadDescriptions",
                  })}
                </SrAlert>
              </Grid>
              <Grid item xs={12}>
                <SrFileInput
                  name="accountStatement"
                  buttonLabel={intl.formatMessage({
                    id: "bankAccounts.choose",
                  })}
                  label={intl.formatMessage({
                    id: "bankAccounts.entry.accountStatement",
                  })}
                  value={values.accountStatement}
                  error={
                    errors.accountStatement?.file && touched.accountStatement
                  }
                  helperText={
                    touched.accountStatement && errors.accountStatement?.file
                  }
                  onChange={(event) => {
                    setFileValue(event, "accountStatement", setFieldValue);
                  }}
                  onBlur={handleBlur}
                  placeholder={intl.formatMessage({
                    id: "bankAccounts.entry.accountStatement",
                  })}
                />
              </Grid>
              <Grid item xs={12}>
                <SrFileInput
                  buttonLabel={intl.formatMessage({
                    id: "bankAccounts.choose",
                  })}
                  error={
                    errors.identificationFront?.file &&
                    touched.identificationFront
                  }
                  fullWidth
                  helperText={
                    touched.identificationFront &&
                    errors.identificationFront?.file
                  }
                  label={intl.formatMessage({
                    id: "bankAccounts.entry.identification.front",
                  })}
                  name="identificationFront"
                  onBlur={handleBlur}
                  onChange={(event) => {
                    setFileValue(event, "identificationFront", setFieldValue);
                  }}
                  placeholder={intl.formatMessage({
                    id: "bankAccounts.entry.identification.front",
                  })}
                  value={values.identificationFront}
                />
              </Grid>
              <Grid item xs={12}>
                <SrFileInput
                  buttonLabel={intl.formatMessage({
                    id: "bankAccounts.choose",
                  })}
                  error={
                    errors.identificationBack?.file &&
                    touched.identificationBack
                  }
                  fullWidth
                  helperText={
                    touched.identificationBack &&
                    errors.identificationBack?.file
                  }
                  label={intl.formatMessage({
                    id: "bankAccounts.entry.identification.back",
                  })}
                  name="identificationBack"
                  onBlur={handleBlur}
                  onChange={(event) => {
                    setFileValue(event, "identificationBack", setFieldValue);
                  }}
                  placeholder={intl.formatMessage({
                    id: "bankAccounts.entry.identification.back",
                  })}
                  value={values.identificationBack}
                />
              </Grid>
              {errorMessage && (
                <Grid item xs={12}>
                  <SrAlert severity="error">{errorMessage}</SrAlert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box textAlign="right">
                  <SrButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    {intl.formatMessage({ id: "common.add" })}
                  </SrButton>
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </SrDialog>
  );
};

CreateBankAccountDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
};

export default CreateBankAccountDialog;
